import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiHeart, FiMail, FiHome, FiUser, FiLogOut } from 'react-icons/fi';
import { GiCoral } from 'react-icons/gi';
import { useAuth } from '../context/AuthContext';

const Nav = styled.nav`
  background: #ffffff;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 105, 148, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  position: relative;
  z-index: 1002;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primaryLight};
  }

  .logo-icon {
    font-size: 2rem;
    color: ${props => props.theme.colors.secondary};
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 1024px) {
    display: none !important;
  }

  @media (max-width: 768px) {
    display: none !important;
  }
`;

const NavLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['active'].includes(prop),
})`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.1);
  }

  ${props => props.active && `
    color: ${props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 30px;
      height: 3px;
      background: ${props.theme.colors.primary};
      border-radius: 2px;
    }
  `}
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 1024px) {
    display: none !important;
  }

  @media (max-width: 768px) {
    display: none !important;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: ${props => props.theme.colors.gray};
  border: none;
  padding: 8px 12px;
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.error};
    background: rgba(239, 68, 68, 0.1);
  }
`;

const MobileNavBar = styled.div`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: white;
  border-top: 2px solid ${props => props.theme.colors.primary};
  z-index: 999999;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 0 5px;
    overflow-x: auto;
    gap: 2px;
  }

  @media (max-width: 480px) {
    height: 65px;
    padding: 0 2px;
  }
`;

const MobileNavItem = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['active'].includes(prop),
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.gray};
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  min-width: 50px;
  flex: 1;
  max-width: 80px;
  text-align: center;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.1);
  }

  .icon {
    font-size: 1.1rem;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  ${props => props.active && `
    color: ${props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.1);
    font-weight: 600;
  `}

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 4px 6px;
    min-width: 45px;
    gap: 1px;

    .icon {
      font-size: 1rem;
    }
  }
`;

const MobileAuthButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.gray};
  font-size: 0.75rem;
  font-weight: 500;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  min-width: 50px;
  flex: 1;
  max-width: 80px;
  cursor: pointer;
  text-align: center;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.1);
  }

  .icon {
    font-size: 1.1rem;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  &.logout-btn:hover {
    color: ${props => props.theme.colors.error};
    background: rgba(239, 68, 68, 0.1);
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 4px 6px;
    min-width: 45px;
    gap: 1px;

    .icon {
      font-size: 1rem;
    }
  }
`;



const Navigation = () => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Nav>
      <NavContainer>
        <Logo to="/">
          <GiCoral className="logo-icon" />
          Riffzauber
        </Logo>

        <NavLinks>
          <NavLink to="/" active={isActive('/')}>
            <FiHome size={18} />
            Startseite
          </NavLink>
          <NavLink to="/korallen" active={isActive('/korallen')}>
            <GiCoral size={18} />
            Korallen
          </NavLink>
          <NavLink to="/fische" active={isActive('/fische')}>
            <FiHeart size={18} />
            Fische
          </NavLink>
          <NavLink to="/kontakt" active={isActive('/kontakt')}>
            <FiMail size={18} />
            Kontakt
          </NavLink>
        </NavLinks>

        <AuthSection>
          {isAuthenticated ? (
            <>
              <UserInfo>
                <FiUser size={18} />
                {user?.username}
              </UserInfo>
              {user?.isAdmin && (
                <NavLink to="/admin" active={isActive('/admin')}>
                  <FiHeart size={18} />
                  Admin
                </NavLink>
              )}
              <LogoutButton onClick={handleLogout}>
                <FiLogOut size={18} />
                Abmelden
              </LogoutButton>
            </>
          ) : (
            <NavLink to="/login" active={isActive('/login')}>
              <FiUser size={18} />
              Anmelden
            </NavLink>
          )}
        </AuthSection>

      </NavContainer>

      <MobileNavBar>
        <MobileNavItem to="/" active={isActive('/')}>
          <FiHome className="icon" />
          <span>Start</span>
        </MobileNavItem>
        <MobileNavItem to="/korallen" active={isActive('/korallen')}>
          <GiCoral className="icon" />
          <span>Korallen</span>
        </MobileNavItem>
        <MobileNavItem to="/fische" active={isActive('/fische')}>
          <FiHeart className="icon" />
          <span>Fische</span>
        </MobileNavItem>
        <MobileNavItem to="/kontakt" active={isActive('/kontakt')}>
          <FiMail className="icon" />
          <span>Kontakt</span>
        </MobileNavItem>
        
        {isAuthenticated ? (
          <>
            {user?.isAdmin && (
              <MobileNavItem to="/admin" active={isActive('/admin')}>
                <FiUser className="icon" />
                <span>Admin</span>
              </MobileNavItem>
            )}
            <MobileAuthButton className="logout-btn" onClick={handleLogout}>
              <FiLogOut className="icon" />
              <span>Logout</span>
            </MobileAuthButton>
          </>
        ) : (
          <MobileNavItem to="/login" active={isActive('/login')}>
            <FiUser className="icon" />
            <span>Login</span>
          </MobileNavItem>
        )}
      </MobileNavBar>
    </Nav>
  );
};

export default Navigation;

